import type { ChangeEvent } from 'react';
import { useId } from 'react';
import { IconeBusca, IconeFechar, IconeSeta } from './Icones.js';
import './Filtros.css';

/** Campo de busca da faixa de filtros. */
export function CampoBusca({
  rotulo,
  valor,
  aoMudar,
  largura = 290,
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly largura?: number;
}) {
  return (
    <label className="campo-busca" style={{ width: `${String(largura)}px` }}>
      <IconeBusca tamanho={15} />
      <span className="apenas-leitor-de-tela">{rotulo}</span>
      <input
        className="campo-busca__entrada"
        type="search"
        placeholder={rotulo}
        value={valor}
        onChange={(evento: ChangeEvent<HTMLInputElement>) => {
          aoMudar(evento.target.value);
        }}
      />
    </label>
  );
}

export type OpcaoFiltro = { readonly valor: string; readonly rotulo: string };

/**
 * Filtro de seleção. É um `<select>` de verdade: o teclado e o leitor de tela
 * já sabem operá-lo, e o supervisor passa o dia inteiro nesta faixa.
 */
export function FiltroSelecao({
  rotulo,
  valor,
  opcoes,
  aoMudar,
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly opcoes: readonly OpcaoFiltro[];
  readonly aoMudar: (valor: string) => void;
}) {
  const id = useId();

  return (
    <span className="filtro-selecao">
      <label className="apenas-leitor-de-tela" htmlFor={id}>
        {rotulo}
      </label>
      <select
        id={id}
        className="filtro-selecao__campo"
        value={valor}
        onChange={(evento: ChangeEvent<HTMLSelectElement>) => {
          aoMudar(evento.target.value);
        }}
      >
        {opcoes.map((opcao) => (
          <option key={opcao.valor} value={opcao.valor}>
            {opcao.rotulo}
          </option>
        ))}
      </select>
      <span className="filtro-selecao__seta">
        <IconeSeta tamanho={13} />
      </span>
    </span>
  );
}

/**
 * Filtro ligado — destacado em azul e removível no próprio chip, para que
 * nunca reste um recorte ativo que a pessoa não percebeu.
 */
export function FiltroAtivo({
  rotulo,
  ativo,
  aoAlternar,
}: {
  readonly rotulo: string;
  readonly ativo: boolean;
  readonly aoAlternar: () => void;
}) {
  return (
    <button
      type="button"
      className={ativo ? 'filtro-ativo filtro-ativo--ligado' : 'filtro-ativo'}
      aria-pressed={ativo}
      onClick={aoAlternar}
    >
      {rotulo}
      {ativo ? <IconeFechar tamanho={13} /> : <IconeSeta tamanho={13} />}
    </button>
  );
}

/** Contagem de registros, encostada à direita da faixa. */
export function ContadorRegistros({ children }: { readonly children: string }) {
  return <span className="contador-registros">{children}</span>;
}

/**
 * Filtro de filial — o código numérico do Senior (`codFil`).
 *
 * É campo de texto, e não seletor, porque o painel não tem de onde tirar a
 * lista de filiais do tenant sem consultar a integração (que só o
 * Administrador lê). Quem chama converte com `filialDoFiltro`: o que não for
 * inteiro positivo não vira recorte.
 */
export function CampoFilial({
  valor,
  aoMudar,
  rotulo = 'Filial',
}: {
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly rotulo?: string;
}) {
  return (
    <label className="campo-busca campo-filial">
      <span className="campo-filial__rotulo">{rotulo}</span>
      <input
        className="campo-busca__entrada"
        type="text"
        inputMode="numeric"
        placeholder="todas"
        aria-label={rotulo}
        value={valor}
        onChange={(evento: ChangeEvent<HTMLInputElement>) => {
          aoMudar(evento.target.value);
        }}
      />
    </label>
  );
}
